/**
 * Price Board: the VN30 constituents first, the four listed VN30F contracts, and whatever the
 * user searched for, streaming live; click a row for its chart, book and an order ticket.
 *
 * Orders placed here belong to no bot. They go straight to the OMS, or for a terminal (CLIENT)
 * account to the user's desktop app, and what fills shows up as unclaimed holdings.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segmented, Select, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLivePrices } from '../../hooks/useLivePrices';
import { usePortfolios } from '../../hooks/usePortfolios';
import useIsMobile from '../../hooks/useIsMobile';
import { BoardOrder, BoardSymbol, priceBoardApi } from '../../services/priceBoardApi';
import BoardTable from './BoardTable';
import BrokerOrdersPanel from './BrokerOrdersPanel';
import OrdersPanel from './OrdersPanel';
import SymbolModal from './SymbolModal';
import SymbolPanel from './SymbolPanel';

type List = 'VN30' | 'FUTURES' | 'WATCH';
const WATCH_KEY = 'priceBoard:watch';

const readWatch = (): BoardSymbol[] => {
    try {
        const v = JSON.parse(localStorage.getItem(WATCH_KEY) ?? '[]');
        return Array.isArray(v) ? v : [];
    } catch {
        return [];
    }
};
const writeWatch = (rows: BoardSymbol[]) => {
    try {
        localStorage.setItem(WATCH_KEY, JSON.stringify(rows));
    } catch {
        // per-browser convenience only
    }
};

const card = {
    // pinned columns paint the card (GlobalStyles)
    '--table-surface': '#1A1D23',
    background: '#1A1D23',
    border: '1px solid #2B3139',
    borderRadius: 6,
    padding: 10,
    minHeight: 0,
} as React.CSSProperties;

/** Digits of equal width and headers on one line, so a tick never moves a column. */
const TABLE_CSS = `
.price-board-table .ant-table { font-variant-numeric: tabular-nums; }
.price-board-table .ant-table-thead > tr > th { white-space: nowrap; }
.price-board-table .ant-table-tbody > tr > td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const PriceBoardPage: React.FC = () => {
    const isMobile = useIsMobile();
    const { portfolios } = usePortfolios();
    const [list, setList] = useState<List>('VN30');
    const [vn30, setVn30] = useState<BoardSymbol[]>([]);
    const [asOf, setAsOf] = useState<string | null>(null);
    const [futures, setFutures] = useState<BoardSymbol[]>([]);
    const [watch, setWatch] = useState<BoardSymbol[]>(readWatch);
    const [loadingLists, setLoadingLists] = useState(true);
    const [selected, setSelected] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.allSettled([priceBoardApi.vn30(), priceBoardApi.futures()]).then(([v, f]) => {
            if (!alive) return;
            if (v.status === 'fulfilled') {
                setVn30(v.value.symbols);
                setAsOf(v.value.asOf);
                if (!isMobile) setSelected((s) => s ?? v.value.symbols[0]?.symbol ?? null);
            }
            if (f.status === 'fulfilled') setFutures(f.value);
            setLoadingLists(false);
        });
        return () => {
            alive = false;
        };
    }, [isMobile]);

    const rows = list === 'VN30' ? vn30 : list === 'FUTURES' ? futures : watch;
    // The visible list streams; the selected symbol does too when it is off-list.
    const prices = useLivePrices(useMemo(() => [...rows.map((r) => r.symbol), selected], [rows, selected]));

    // ---- search: any stock, or one of the four contracts
    const [options, setOptions] = useState<BoardSymbol[]>([]);
    const [searching, setSearching] = useState(false);
    const timer = useRef<number | undefined>(undefined);
    const onSearch = (q: string) => {
        window.clearTimeout(timer.current);
        if (!q.trim()) {
            setOptions([]);
            return;
        }
        timer.current = window.setTimeout(async () => {
            setSearching(true);
            try {
                setOptions(await priceBoardApi.search(q));
            } catch {
                setOptions([]);
            } finally {
                setSearching(false);
            }
        }, 300);
    };
    const pick = (symbol: string) => {
        const hit = options.find((o) => o.symbol === symbol);
        if (hit && !vn30.some((r) => r.symbol === symbol) && !futures.some((r) => r.symbol === symbol)) {
            const next = [hit, ...watch.filter((w) => w.symbol !== symbol)].slice(0, 30);
            setWatch(next);
            writeWatch(next);
            setList('WATCH');
        } else if (futures.some((r) => r.symbol === symbol)) {
            setList('FUTURES');
        } else {
            setList('VN30');
        }
        // A search pick opens the modal on a phone only; the desktop side panel already shows it.
        setSelected(symbol);
        if (isMobile) setModalOpen(true);
    };
    // A removed symbol that is selected stays in the side panel (it still streams as off-list).
    const removeWatch = (symbol: string) => {
        const next = watch.filter((w) => w.symbol !== symbol);
        setWatch(next);
        writeWatch(next);
    };
    const onRemove = list === 'WATCH' ? removeWatch : undefined;
    const select = (symbol: string) => {
        setSelected(symbol);
        setModalOpen(true);
    };

    // ---- orders: polled, faster while any is still working
    const [orders, setOrders] = useState<BoardOrder[]>([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const loadOrders = useCallback(async () => {
        try {
            setOrders(await priceBoardApi.orders());
        } catch {
            // keep what is on screen
        } finally {
            setLoadingOrders(false);
        }
    }, []);
    const anyOpen = orders.some((o) => o.open);
    useEffect(() => {
        void loadOrders();
        const id = window.setInterval(() => void loadOrders(), anyOpen ? 3000 : 15000);
        return () => window.clearInterval(id);
    }, [loadOrders, anyOpen]);
    const accountName = useCallback(
        (id: string) => portfolios.find((p) => p.id === id)?.portfolioName ?? id,
        [portfolios],
    );

    // Orders card: today's board orders, or what is still live at the broker for one account.
    const [ordersTab, setOrdersTab] = useState<'TODAY' | 'BROKER'>('TODAY');
    const ordersHeader = (title: string) => (
        <Segmented
            size="small"
            style={{ marginBottom: 6 }}
            value={ordersTab}
            onChange={(v) => setOrdersTab(v as 'TODAY' | 'BROKER')}
            options={[
                { label: title, value: 'TODAY' },
                { label: 'Live at broker', value: 'BROKER' },
            ]}
        />
    );
    const ordersBody = (compact: boolean) =>
        ordersTab === 'BROKER' ? (
            <BrokerOrdersPanel portfolios={portfolios} compact={compact} />
        ) : (
            <OrdersPanel orders={orders} loading={loadingOrders} compact={compact} accountName={accountName} onChanged={() => void loadOrders()} />
        );

    const search = (
        <Select<string>
            showSearch
            value={undefined}
            placeholder={<span><SearchOutlined /> Search a stock or VN30F…</span>}
            filterOption={false}
            onSearch={onSearch}
            onSelect={(v: string) => pick(v)}
            notFoundContent={searching ? <Spin size="small" /> : null}
            options={options.map((o) => ({
                value: o.symbol,
                label: `${o.symbol}${o.tenor ? ` · ${o.tenor}` : ''} · ${o.exchange}`,
            }))}
            style={{ width: isMobile ? '100%' : 260 }}
        />
    );
    const tabs = (
        <Segmented
            block={isMobile}
            value={list}
            onChange={(v) => setList(v as List)}
            options={[
                { label: isMobile || !asOf ? 'VN30' : `VN30 (${asOf})`, value: 'VN30' },
                { label: 'Futures', value: 'FUTURES' },
                { label: `${isMobile ? 'Watch' : 'Watchlist'}${watch.length ? ` (${watch.length})` : ''}`, value: 'WATCH' },
            ]}
        />
    );
    const panel = selected ? (
        <SymbolPanel symbol={selected} quote={prices[selected]} portfolios={portfolios} onPlaced={() => void loadOrders()} />
    ) : (
        <div style={{ color: '#848E9C' }}>Pick a symbol</div>
    );
    const modal = (
        <SymbolModal
            symbol={selected}
            open={modalOpen}
            quote={selected ? prices[selected] : undefined}
            portfolios={portfolios}
            onPlaced={() => void loadOrders()}
            onClose={() => setModalOpen(false)}
            mobile={isMobile}
        />
    );

    if (isMobile) {
        return (
            <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 10, background: '#0B0E11' }}>
                <style>{TABLE_CSS}</style>
                {/* Search and list switch stay reachable while the list scrolls. */}
                <div style={{ position: 'sticky', top: 0, zIndex: 3, flex: 'none', background: '#0B0E11', padding: '12px 0 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>Price Board</div>
                    {search}
                    {tabs}
                </div>
                {/* flex: none — in this fixed-height column a shrinkable card squeezed the board
                    and let the orders card draw over it. */}
                <div style={{ ...card, padding: 6, flex: 'none' }}>
                    {list === 'VN30' && asOf && <div style={{ color: '#848E9C', fontSize: 11, padding: '0 4px 4px' }}>Constituents as of {asOf} · tap a row to trade</div>}
                    <BoardTable rows={rows} prices={prices} selected={selected} onSelect={select} onRemove={onRemove} compact loading={loadingLists} />
                </div>
                <div style={{ ...card, flex: 'none' }}>
                    {ordersHeader('Orders')}
                    {ordersBody(true)}
                </div>
                {modal}
            </div>
        );
    }

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 12, gap: 10, background: 'linear-gradient(180deg, #0B0E11 0%, #12161C 100%)' }}>
            <style>{TABLE_CSS}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>Price Board</span>
                {tabs}
                <div style={{ marginLeft: 'auto' }}>{search}</div>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ ...card, flex: 1, overflow: 'auto' }}>
                        <BoardTable rows={rows} prices={prices} selected={selected} onSelect={select} onRemove={onRemove} loading={loadingLists} />
                    </div>
                    <div style={{ ...card, height: 240, overflow: 'auto' }}>
                        {ordersHeader('Orders today')}
                        {ordersBody(false)}
                    </div>
                </div>
                <div style={{ ...card, width: 340, flexShrink: 0, overflow: 'auto' }}>{panel}</div>
            </div>
            {modal}
        </div>
    );
};

export default PriceBoardPage;
